import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ConnectButton } from "@thema/ui/wallet";
import {
  useWallet,
  useConsent,
  useVaultConfig,
  useVaultAssetMetadata,
  useWalletCapStatus,
  Modal,
} from "@thema/ui";

const ACK_STORAGE_KEY = "thema:early-access:acknowledged";
const EMAIL_STORAGE_KEY = "thema:early-access:email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CONSENT_WEBHOOK_URL =
  "https://script.google.com/macros/s/AKfycbxBE7vyXrGiaLSumrMf9HImEM_ATwWLhXtfULmJaU2-kZNcFqkeiVx4lbTfjiagFyHpoQ/exec";
const CONSENT_VERSION = "v1";

type ConsentRecordPayload = {
  wallet: string;
  email: string;
  vault: string;
  chainId: string;
  consentVersion: string;
  consentHash: string;
  status: "new-consent" | "revisit";
};

// text/plain bypasses the CORS preflight that Google Apps Script web apps
// don't answer; the doPost handler still reads e.postData.contents as JSON.
async function postConsentRecord(payload: ConsentRecordPayload): Promise<void> {
  try {
    await fetch(CONSENT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.warn("[consent] webhook POST failed", err);
  }
}

import { CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF } from "./consent-config";

export default function Consent() {
  const navigate = useNavigate();
  const { isConnected, shortAddress, address } = useWallet();
  const vault = useVaultConfig(CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF)
  if (!vault) {
    throw new Error(
      `Vault config missing for deployment "${CONSENT_DEPLOYMENT_ID}" — generated registry must include a vault descriptor.`,
    );
  }
  const {
    isInitialized,
    hashesMatch,
    hasConsented,
    isOnCorrectChain,
    switchToVaultChain,
    recordConsent,
    status,
    txHash,
    txExplorerUrl,
    error,
    statements,
    isLoading,
    fetchError,
    vaultMissingConsentModule,
    expectedHash,
    localHash,
  } = useConsent({ vault });

  // Whitelist gate — drives the "Next Step" routing for already-consented wallets.
  const walletAddr = address as `0x${string}` | undefined;
  const { metadata: assetMeta } = useVaultAssetMetadata({ vault });
  const capDecimals = assetMeta
    ? {
        assetDecimals: assetMeta.asset.decimals,
        accountingDecimals: assetMeta.accountingBase.decimals,
      }
    : undefined;
  const { status: capStatus, isLoading: capLoading } = useWalletCapStatus({
    vault,
    wallet: walletAddr,
    decimals: capDecimals,
  });
  const isWhitelisted = capStatus?.configured === true;

  // Bounce users who skipped the disclosure straight to step 1.
  let acknowledged = false;
  try {
    acknowledged = sessionStorage.getItem(ACK_STORAGE_KEY) === "1";
  } catch {
    /* sessionStorage unavailable */
  }

  const [email, setEmail] = useState(() => {
    try {
      return sessionStorage.getItem(EMAIL_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const trimmedEmail = email.trim();
  const isValidEmail = EMAIL_REGEX.test(trimmedEmail);
  const showEmailError = email.length > 0 && !isValidEmail;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revisitSubmitting, setRevisitSubmitting] = useState(false);

  const onEmailChange = (value: string) => {
    setEmail(value);
    try {
      if (value) sessionStorage.setItem(EMAIL_STORAGE_KEY, value);
      else sessionStorage.removeItem(EMAIL_STORAGE_KEY);
    } catch {
      /* sessionStorage unavailable */
    }
  };

  // Once the on-chain receipt confirms, POST the consent record then advance
  // to the done screen. The ref guards against the effect re-firing (would
  // otherwise duplicate the webhook call).
  // Hook MUST run before any conditional return to satisfy the rules of hooks.
  const newConsentSentRef = useRef(false);
  useEffect(() => {
    if (status !== "confirmed" || !txHash) return;
    if (newConsentSentRef.current) return;
    newConsentSentRef.current = true;

    void (async () => {
      await postConsentRecord({
        wallet: address ?? "",
        email: trimmedEmail,
        vault: vault.vaultAddress,
        chainId: vault.chainId,
        consentVersion: CONSENT_VERSION,
        consentHash: expectedHash ?? localHash,
        status: "new-consent",
      });
      const params = new URLSearchParams({
        tx: txHash,
        ...(address ? { wallet: address } : {}),
      });
      navigate(`/early-access/done?${params.toString()}`, { replace: true });
    })();
  }, [
    status,
    txHash,
    address,
    navigate,
    trimmedEmail,
    vault.vaultAddress,
    vault.chainId,
    expectedHash,
    localHash,
  ]);

  if (!acknowledged) return <Navigate to="/early-access" replace />;

  const onRequestSign = () => {
    if (!isValidEmail) return;
    setConfirmOpen(true);
  };

  const onConfirmSign = async () => {
    if (!isValidEmail) return;
    setConfirmOpen(false);
    try {
      await recordConsent();
    } catch {
      /* surfaced via `error` */
    }
  };

  // Already consented in a prior session — POST a revisit record so the
  // backend captures the email against the existing on-chain consent, then
  // route forward based on whitelist.
  const onNextStep = async () => {
    if (!walletAddr || !isValidEmail || revisitSubmitting) return;
    setRevisitSubmitting(true);
    try {
      await postConsentRecord({
        wallet: walletAddr,
        email: trimmedEmail,
        vault: vault.vaultAddress,
        chainId: vault.chainId,
        consentVersion: "",
        consentHash: "",
        status: "revisit",
      });
    } finally {
      setRevisitSubmitting(false);
    }
    if (isWhitelisted) {
      navigate(`/welcome-${walletAddr}`);
    } else {
      const params = new URLSearchParams({
        ...(txHash ? { tx: txHash } : {}),
        wallet: walletAddr,
      });
      const qs = params.toString();
      navigate(`/early-access/done${qs ? `?${qs}` : ""}`);
    }
  };

  const canSign =
    isConnected &&
    isOnCorrectChain &&
    isInitialized === true &&
    hashesMatch === true &&
    !hasConsented &&
    status !== "awaiting-signature" &&
    status !== "mining" &&
    isValidEmail;

  return (
    <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
      <article className="mx-auto max-w-[560px] space-y-8">
        <header className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            STEP 2 OF 2 &middot; ON-CHAIN CONSENT
          </p>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
            Sign your acknowledgement
          </h1>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/80">
            Connect a wallet on {vault.chainName}. The transaction calls{" "}
            <code className="text-white">recordConsent(hash)</code> on the vault
            and emits a public <code className="text-white">ConsentRecorded</code>{" "}
            event. There is no payment, no approval, and no fund movement.
          </p>
        </header>

        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
              Vault
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-teal/80">
              {vault.omtName} &middot; {vault.chainName}
            </span>
          </div>
          <p className="text-[13px] text-thema-text-soft/85 leading-relaxed">
            This vault is for{" "}
            <span className="text-white">{vault.omtName}</span> OMT on{" "}
            <span className="text-white">{vault.chainName}</span>. Currently
            live at:
          </p>
          <a
            href={`https://basescan.org/address/${vault.vaultAddress}`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block font-mono text-[12px] text-thema-royal-blue hover:underline break-all"
          >
            {vault.vaultAddress}
          </a>
        </section>

        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-4 space-y-3">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
            You are attesting to
          </h2>
          <ul className="space-y-1.5 text-[13px] text-thema-text-soft/85 pl-4 list-disc marker:text-white/30">
            {statements.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </section>

        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
              Email
            </h2>
            {showEmailError && (
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-300/80">
                Invalid format
              </span>
            )}
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
            required
            aria-invalid={showEmailError}
            className="w-full bg-white/[0.025] border border-thema-border-hairline rounded-sm px-3 py-2 text-[13px] text-white placeholder:text-thema-micro focus:outline-none focus:border-thema-royal-blue/60"
          />
          <p className="text-[11px] leading-relaxed text-thema-text-soft/65">
            Used to send whitelist confirmation and next-step instructions.
          </p>
        </section>

        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 min-h-[40px]">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro shrink-0">
                Wallet
              </span>
              <span
                className={
                  "text-[13px] truncate " +
                  (isConnected ? "text-thema-text-soft" : "text-thema-micro")
                }
              >
                {isConnected ? shortAddress : "Not connected"}
              </span>
            </div>
            <ConnectButton variant="outline" size="sm" />
          </div>

          {isConnected && !isOnCorrectChain && (
            <div className="flex items-center justify-between gap-3 rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[12px]">
              <span className="text-amber-200/90">
                Wrong network. Switch to {vault.chainName} to continue.
              </span>
              <button
                type="button"
                onClick={() => switchToVaultChain().catch(() => {})}
                className="text-amber-100 hover:underline"
              >
                Switch
              </button>
            </div>
          )}

          {vaultMissingConsentModule && (
            <div className="rounded-sm border border-red-500/40 bg-red-500/5 px-3 py-2 text-[12px] text-red-200/90 space-y-1">
              <p className="font-medium">
                The on-chain consent module isn&rsquo;t available on this vault yet.
              </p>
              <p className="leading-relaxed text-red-200/70">
                The deployed vault at this address predates the{" "}
                <code>recordConsent</code> patch. A patched vault needs to be
                deployed and its hash set via{" "}
                <code>setConsentAcknowledgementsHash</code> before consent can be
                signed.
              </p>
            </div>
          )}

          {isLoading && !vaultMissingConsentModule && (
            <p className="text-[12px] text-thema-micro">
              Checking on-chain state…
            </p>
          )}

          {!isLoading && !vaultMissingConsentModule && fetchError && (
            <p className="text-[12px] text-red-300/90 break-words">
              Could not read vault state: {fetchError.message}
            </p>
          )}

          {isConnected && isOnCorrectChain && !isLoading && !vaultMissingConsentModule && isInitialized === false && (
            <p className="text-[12px] text-amber-200/90">
              The vault has not yet had its consent hash initialized by the
              owner. Try again shortly.
            </p>
          )}

          {isConnected && isOnCorrectChain && !isLoading && hashesMatch === false && isInitialized === true && (
            <p className="text-[12px] text-amber-200/90">
              The acknowledgement text shown here is out of sync with the
              contract. Refresh the page and re-read the disclosure.
            </p>
          )}

          {hasConsented && (
            <p className="text-[12px] text-emerald-300/90">
              This wallet has already recorded consent against the current
              acknowledgements hash.
            </p>
          )}

          {error && !vaultMissingConsentModule && (
            <p className="text-[12px] text-red-300/90 break-words">
              {error.message}
            </p>
          )}

          <button
            type="button"
            onClick={hasConsented ? onNextStep : onRequestSign}
            disabled={hasConsented ? capLoading || !walletAddr || !isValidEmail || revisitSubmitting : !canSign}
            className="w-full px-5 py-3 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium transition hover:bg-thema-royal-blue/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-thema-royal-blue/15"
          >
            {status === "awaiting-signature"
              ? "Confirm in your wallet…"
              : status === "mining"
              ? "Waiting for confirmation…"
              : hasConsented
              ? capLoading
                ? "Checking access…"
                : !isValidEmail
                ? "Enter your email to continue"
                : revisitSubmitting
                ? "Recording email…"
                : "Next Step →"
              : vaultMissingConsentModule
              ? "Consent module unavailable on this vault"
              : !isConnected
              ? "Connect a wallet to continue"
              : !isOnCorrectChain
              ? `Switch to ${vault.chainName} to continue`
              : isLoading
              ? "Loading on-chain state…"
              : isInitialized === false
              ? "Awaiting owner initialisation"
              : hashesMatch === false
              ? "Acknowledgement out of sync — refresh"
              : !isValidEmail
              ? "Enter your email to continue"
              : "Sign Consent Transaction"}
          </button>

          {txHash && (
            <p className="text-[11px] font-mono text-thema-micro break-all">
              tx:{" "}
              {txExplorerUrl ? (
                <a
                  href={txExplorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-thema-royal-blue hover:underline"
                >
                  {txHash}
                </a>
              ) : (
                txHash
              )}
            </p>
          )}
        </section>

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/early-access"
            className="font-mono text-[11px] uppercase tracking-[0.15em] text-thema-micro hover:text-white/70"
          >
            ← Re-read disclosure
          </Link>
        </div>
      </article>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm your email"
        width={480}
      >
        <div className="space-y-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
            This email will be bound to your wallet consent
          </p>

          <div className="rounded-md border border-thema-border-hairline bg-white/[0.025] p-4 space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
              Email
            </p>
            <p className="text-[14px] text-white break-all">{trimmedEmail}</p>
          </div>

          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
            <p className="text-[13px] leading-relaxed text-amber-200/90">
              This email will be recorded alongside your wallet consent and
              <span className="text-white"> cannot be changed later</span>.
            </p>
            <p className="text-[13px] leading-relaxed text-amber-200/80">
              All vault-related communications — whitelist confirmation,
              next-step instructions, and future operational updates — will be
              sent to this address only.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 rounded-md border border-thema-border-hairline text-thema-text-soft text-sm hover:bg-white/5 transition"
            >
              Change email
            </button>
            <button
              type="button"
              onClick={onConfirmSign}
              className="px-5 py-2 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium hover:bg-thema-royal-blue/25 transition"
            >
              Confirm &amp; sign →
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
}
